/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * LEGACY PARITY FILE - DO NOT REFACTOR
 * This file maintains 100% functional parity with:
 * /lego/services/workflow-service/useWorkflowService.jsx
 *
 * See docs/LEGACY_WORKFLOW_SERVICE_ANALYSIS.md for detailed behavior documentation.
 */

import React from 'react'
import cloneDeep from 'lodash/cloneDeep'
import find from 'lodash/find'
import includes from 'lodash/includes'
import reduce from 'lodash/reduce'
import extend from 'lodash/extend'
import isArray from 'lodash/isArray'
import { useServiceContext } from '../../root'
import { WorkflowStepType, WorkflowType } from '../../constants/workflow'
import { TaskStatus } from '../task.service'
import { useModal } from '../../contexts/ModalContext'
import { baseApiService } from '../api/base'
import { toast } from '../toast'
import type { Task } from '../api/types'
import type { DetailedWorkflow } from '../api/workflows'
import { useEsignature } from '../../hooks/useEsignature'

// ============================================================================
// GLOBAL STATE VARIABLES (Module-level, persists across function calls)
// ============================================================================
// LEGACY PARITY: These MUST remain module-level variables, NOT React state

let isDirectFiling = false
let artifactIdForDirectFiling: string | number | null = null
let isFromNewFileToEtmf = false
let propertiesValuesGlobal: Record<string, any> | null = null
let isModalBackgroundClickableGlobal = false

// ============================================================================
// TYPES
// ============================================================================

interface CreateCrRequest {
  description?: string
  fileName?: string
  documentTypeTemplateId?: { id?: number; value?: number } | number | null
  parentId?: number
  type: string
  targetFsiId?: number
  fileId?: number
  fileIds?: number[]
  sourceFiles?: any[]
  obsoleteSourceDocumentUponApproval?: boolean
  expirationDate?: Date | null
  isAmendmentAddition?: boolean
  prRemediationChangeReq?: boolean
  justificationTextId?: { value: number } | null
  customJustificationText?: string
  fileAs?: string
  groupTargetFsiId?: number
}

interface ApproversType {
  initialApprovers?: { type: string; steps?: any[] }
  collaborators?: { type: string; steps?: any[] }
  approvers?: { type: string; steps?: any[] }
  reviewers?: { type: string; steps?: any[] }
}

interface RawMetadataItem {
  id: number
  name: string
  type: string
}

interface DeferredPromise<T = any> {
  promise: Promise<T>
  resolve: (value?: T | PromiseLike<T>) => void
  reject: (reason?: any) => void
}

// ============================================================================
// CreateCrParams - Object-based API for createCr (NEW for esignature support)
// ============================================================================

export interface CreateCrParams {
  req: CreateCrRequest | any
  type?: ApproversType
  fileToUpload?: File | null
  directFiling?: boolean
  artifactId?: string | number | null
  isModalBackgroundClickable?: boolean
  isFromNewFileToEtmfValue?: boolean
  propertiesValues?: Record<string, any> | null
  rawMetadata?: RawMetadataItem[] | null
  docTitle?: string
}

// ============================================================================
// DEFERRED PROMISE UTILITY (Legacy $q.defer() pattern)
// ============================================================================

function createDeferred<T = any>(): DeferredPromise<T> {
  let resolve!: (value?: T | PromiseLike<T>) => void
  let reject!: (reason?: any) => void

  const promise = new Promise<T>((res, rej) => {
    resolve = res as (value?: T | PromiseLike<T>) => void
    reject = rej
  })

  return { promise, resolve, reject }
}

// ============================================================================
// METADATA TYPE CONSTANTS
// ============================================================================

const MetaType = {
  ARTIFACT: 'ARTIFACT',
  DATETIME: 'DATETIME'
}

// ============================================================================
// transformDateForUI - transforms date for UI display
// ============================================================================

function transformDateForUI(date: any): number | string | null {
  if (!date) return null
  if (typeof date === 'string' || typeof date === 'number') return date
  if (date instanceof Date) return date.getTime()
  return date
}

// ============================================================================
// MAIN HOOK
// ============================================================================

function useWorkflowServiceNew() {
  const context = useServiceContext()
  const { showModal, hideModal } = useModal()
  const { signAction } = useEsignature()

  // ============================================================================
  // isEosWf - Check if workflow is EOS type
  // LEGACY PARITY: Uses lodash _.contains pattern (same as _.includes)
  // ============================================================================
  const isEosWf: any = (type: string): boolean => {
    return includes([WorkflowType.EOS_DOWNLOAD_AND_ACKNOWLEDGE], type)
  }

  // ============================================================================
  // getWorkingFileFromWF - Gets working file from workflow DTO
  // LEGACY PARITY: Checks draftFsi -> targetFsi -> sourceFsi in order
  // ============================================================================
  const getWorkingFileFromWF = (wfDto: DetailedWorkflow): any | null => {
    if (!wfDto.files || !wfDto.files[0]) return null

    if (wfDto.files[0].draftFsi) {
      return wfDto.files[0].draftFsi
    } else if (wfDto.files[0].targetFsi) {
      return wfDto.files[0].targetFsi
    } else if (wfDto.files[0].sourceFsi) {
      return wfDto.files[0].sourceFsi
    }
    return null
  }

  // ============================================================================
  // createCr - Main workflow creation function
  // LEGACY PARITY: Lines 23-180 in useWorkflowService.jsx
  //
  // Usage (Object API):
  //   createCr({ req, type, fileToUpload, directFiling, ... })
  // ============================================================================
  const createCr = async (params: CreateCrParams): Promise<any> => {
    // Extract from object-based API
    const {
      req,
      type: typeParam = {},
      fileToUpload: fileToUploadParam = null,
      directFiling: directFilingParam = false,
      artifactId: artifactIdParam = null,
      isModalBackgroundClickable: isModalBackgroundClickableParam = false,
      isFromNewFileToEtmfValue: isFromNewFileToEtmfValueParam = false,
      propertiesValues: propertiesValuesParam = null,
      rawMetadata: rawMetadataParam = null,
      docTitle: docTitleParam
    } = params

    // Use local variables for the rest of the function
    const type = typeParam
    const fileToUpload = fileToUploadParam
    const directFiling = directFilingParam
    const artifactId = artifactIdParam
    const isModalBackgroundClickable = isModalBackgroundClickableParam
    const isFromNewFileToEtmfValue = isFromNewFileToEtmfValueParam
    const propertiesValues = propertiesValuesParam
    const rawMetadata = rawMetadataParam
    const docTitle = docTitleParam

    // Set global state variables (LEGACY PARITY)
    propertiesValuesGlobal = propertiesValues ?? null
    isDirectFiling = directFiling ?? false
    artifactIdForDirectFiling = artifactId ?? null
    isModalBackgroundClickableGlobal = isModalBackgroundClickable ?? false
    isFromNewFileToEtmf = isFromNewFileToEtmfValue ?? false

    const metadatasDTO: { metadataTemplateId: number; value: any }[] = []

    // Build request object (LEGACY PARITY: exact same structure)
    const requestToSend: Record<string, any> = {
      collaborators: null,
      initialApprovers: null,
      approvers: null,
      reviewers: null,
      justificationTextId: null,
      customJustificationText: null,
      description: req.description,
      fileName: req.fileName || docTitle,
      documentTypeTemplateId: req.documentTypeTemplateId
        ? (req.documentTypeTemplateId as any).id
          ? (req.documentTypeTemplateId as any).id
          : (req.documentTypeTemplateId as any).value
            ? (req.documentTypeTemplateId as any).value
            : null
        : null,
      parentFolderId: req.parentId,
      workflowType: req.type,
      targetFsiId: req.targetFsiId,
      fileId: req.fileId,
      fileIds: req.fileIds,
      sourceFiles: req.sourceFiles,
      obsoleteSourceDocumentUponApproval: req.obsoleteSourceDocumentUponApproval,
      expirationDate: req.expirationDate && req.expirationDate instanceof Date ? req.expirationDate.getTime() : null,
      isAmendmentAddition: req.isAmendmentAddition,
      prRemediationChangeReq: req.prRemediationChangeReq
    }

    // Handle justification text (LEGACY PARITY)
    if (req.justificationTextId) {
      if ((req.justificationTextId as any).value === 0) {
        requestToSend.justificationTextId = null
        requestToSend.customJustificationText = req.customJustificationText
      } else {
        requestToSend.justificationTextId = (req.justificationTextId as any).value
        requestToSend.customJustificationText = null
      }
    }

    // Assemble approvers (LEGACY PARITY: using context.api.files assembly functions)
    const initialApprovers: any[] = []

    if (
      (type.initialApprovers && type.initialApprovers.type === 'SETUP_WITH_WORKFLOW') ||
      (type.initialApprovers && type.initialApprovers.type === 'USE_PREDEFINED')
    ) {
      const assembled = context.api.files.assembleFsiCRApproversForServer?.(type)
      if (assembled?.initialApprovers?.steps) {
        initialApprovers.push(...assembled.initialApprovers.steps)
      }
    }

    if (type.initialApprovers && type.initialApprovers.type === 'SETUP_WITH_WORKFLOW') {
      requestToSend.initialApprovers = initialApprovers
    }
    if (type.collaborators && type.collaborators.type === 'SETUP_WITH_WORKFLOW') {
      const assembled = context.api.files.assembleFsiEditorsForServer?.(type)
      requestToSend.collaborators = assembled?.collaborators?.steps
    }
    if (type.approvers && type.approvers.type === 'SETUP_WITH_WORKFLOW') {
      const assembled = context.api.files.assembleFsiApproversForServer?.(type)
      requestToSend.approvers = assembled?.approvers?.steps
    }
    if (type.reviewers && type.reviewers.type === 'SETUP_WITH_WORKFLOW') {
      const assembled = context.api.files.assembleFsiReviewersForServer?.(type)
      requestToSend.reviewers = assembled?.reviewers?.steps
    }

    // ========================================================================
    // FILE_ARTIFACT WORKFLOW (eTMF) - Lines 97-137 in legacy
    // ========================================================================
    if (requestToSend.workflowType === 'FILE_ARTIFACT') {
      const def = createDeferred()

      // Build metadatasDTO (LEGACY PARITY: exact same switch logic)
      rawMetadata?.forEach((item) => {
        switch (item.type) {
          case MetaType.ARTIFACT:
            // LEGACY PARITY: Only push if isDirectFiling
            if (isDirectFiling) {
              metadatasDTO.push({
                metadataTemplateId: item.id,
                value: artifactIdForDirectFiling
              })
            }
            break
          case MetaType.DATETIME:
            metadatasDTO.push({
              metadataTemplateId: item.id,
              value: propertiesValuesGlobal?.[item.name]
                ? transformDateForUI(cloneDeep(propertiesValuesGlobal[item.name]))
                : null
            })
            break
          default:
            metadatasDTO.push({
              metadataTemplateId: item.id,
              value: propertiesValuesGlobal?.[item.name]
            })
            break
        }
      })

      try {
        // Call eTMF endpoint (LEGACY PARITY)
        await baseApiService.fetch('/api/etmf/v2/documentchanges', {
          method: 'POST',
          body: {
            ...requestToSend,
            metadatas: metadatasDTO,
            effective: req?.fileAs === 'CURRENT',
            ...(req?.groupTargetFsiId ? { groupTargetFsiId: req.groupTargetFsiId } : {})
          }
        })
        toast.info('FILE_DOC_TO_ETMF_BG_JOB_SUCCESS_INFO_MSG')
        def.resolve()
      } catch (e) {
        toast.error('FILE_DOC_TO_ETMF_BG_JOB_FAIL_MSG')
        def.reject()
      }

      return def.promise
    }

    // ========================================================================
    // OTHER WORKFLOW TYPES (NEW, CHANGE, RECLASSIFY_DOCUMENT, etc.)
    // Lines 138-180 in legacy
    // ========================================================================
    else {
      const def = createDeferred()

      try {
        // Call standard workflow endpoint
        const resp = await baseApiService.fetch<any>('/api/workflows', {
          method: 'POST',
          body: requestToSend
        })

        const postFlow = createDeferred()
        def.resolve(postFlow)

        const createWfResp = resp

        // Show warning if present (LEGACY PARITY)
        if (createWfResp.warning) {
          toast.success(createWfResp.warning)
        }

        const showSuccessMsg = () => {
          toast.success(
            context.service.filter.translate('BROWSER_ADD_FILE_SUCCESS_MSG', { workflowNo: createWfResp.workflowNo })
          )
          // Emit updateTables event (LEGACY PARITY: $rootScope.$emit)
          window.dispatchEvent(new CustomEvent('updateTables'))
          postFlow.resolve()
        }

        console.log('just outside createWfResp.id')
        if (createWfResp.id) {
          console.log('inside createWfResp.id')
          try {
            // Get full workflow (LEGACY PARITY: api.workflows.getById)
            const wf = await baseApiService.fetch<DetailedWorkflow>(`/api/workflows/${createWfResp.id}`)

            console.log('inside includes NEW or CHANGE', requestToSend.workflowType)
            // RECLASSIFY_DOCUMENT special flow (LEGACY PARITY)
            if (WorkflowType.RECLASSIFY_DOCUMENT === requestToSend.workflowType) {
              try {
                await runPostUploadSequence(wf)
                await autorunTaskApprovals(wf, fileToUpload)
              } finally {
                showSuccessMsg()
              }
            }
            // NEW or CHANGE workflow (LEGACY PARITY)
            else if (includes([WorkflowType.NEW, WorkflowType.CHANGE], requestToSend.workflowType)) {
              try {
                console.log('[DEBUG] yes inside includes NEW or CHANGE')
                console.log('[DEBUG] Ye wala fetch wf', wf)
                console.log('[DEBUG] wf.id =', wf.id)
                console.log('[DEBUG] fileToUpload =', fileToUpload)
                console.log('[DEBUG] About to call autorunTaskApprovals...')
                await autorunTaskApprovals(wf, fileToUpload)
                console.log('[DEBUG] autorunTaskApprovals completed successfully')
              } finally {
                showSuccessMsg()
              }
            }
          } catch (e) {
            console.log('[DEBUG] error inside createWfResp.id catch', e)
            console.log('[DEBUG] Error type:', typeof e)
            console.log('[DEBUG] Error message:', e instanceof Error ? e.message : String(e))
            console.log('[DEBUG] Error stack:', e instanceof Error ? e.stack : 'N/A')
            postFlow.reject(e)
          }
        }
      } catch (data) {
        // Create CR failed (LEGACY PARITY: service.error.showDefault)
        def.reject(data)
      }

      return def.promise
    }
  }

  // ============================================================================
  // autorunTaskApprovals - Recursive task approval function
  // LEGACY PARITY: Lines 182-243 - exact same switch/case structure
  // ============================================================================
  const autorunTaskApprovals = (wf: DetailedWorkflow, fileToUpload?: File | null): Promise<void> => {
    console.log('[DEBUG] autorunTaskApprovals called with wf.id =', wf.id)
    const def = createDeferred<void>()

    console.log('[DEBUG] Calling getActiveTaskForWFStepType...')
    getActiveTaskForWFStepType(wf.id)
      .then((task: Task) => {
        console.log('[DEBUG] getActiveTaskForWFStepType resolved with task:', task)
        console.log('[DEBUG] task.workflowStepType =', task.workflowStepType)
        console.log('[DEBUG] task.id =', task.id)
        // LEGACY PARITY: IIFE pattern for switch statement
        const taskApproval = (function () {
          console.log('[DEBUG] Entering switch for workflowStepType:', task.workflowStepType)
          switch (task.workflowStepType) {
            case WorkflowStepType.AUTHORIZE_PLACEHOLDER_CREATION:
            case WorkflowStepType.AUTHORIZE_REFERENCE_CREATION:
            case WorkflowStepType.AUTHORIZE_MIGRATION:
            case WorkflowStepType.AUTHORIZE_FILL_PLACEHOLDER:
            case WorkflowStepType.AUTHORIZE_RECLASSIFICATION:
            case WorkflowStepType.AUTHORIZE_CHANGE_REQUEST:
            case WorkflowStepType.APPROVE_OBSOLETE:
              console.log('[DEBUG] Switch matched AUTHORIZE case, calling performStageAuthorize')
              return performStageAuthorize(task)

            case WorkflowStepType.REVISE_EDIT:
            case WorkflowStepType.REVISE_EDIT_MULTIFILE:
            case WorkflowStepType.REVISE_EDIT_EOS:
            case WorkflowStepType.REVISE_EDIT_MIGRATION:
            case WorkflowStepType.REVISE_EDIT_RECLASSIFICATION:
            case WorkflowStepType.REVISE_EDIT_FILE_ARTIFACT:
              console.log('[DEBUG] Switch matched REVISE_EDIT case, calling performStageEdit')
              return performStageEdit(wf, task, fileToUpload || undefined)

            case WorkflowStepType.APPROVE_REFERENCE_CREATION:
            case WorkflowStepType.APPROVE_PLACEHOLDER_CREATION:
            case WorkflowStepType.APPROVE_OBSOLETING_REFERENCE:
            case WorkflowStepType.REVIEW:
            case WorkflowStepType.REVIEW_MULTIFILE:
            case WorkflowStepType.REVIEW_EOS:
            case WorkflowStepType.REVIEW_RECLASSIFICATION:
            case WorkflowStepType.REVIEW_FILE_ARTIFACT:
            case WorkflowStepType.APPROVE_MULTIFILE:
            case WorkflowStepType.APPROVE_MIGRATION:
            case WorkflowStepType.APPROVE:
            case WorkflowStepType.APPROVE_EOS:
            case WorkflowStepType.APPROVE_RECLASSIFICATION:
            case WorkflowStepType.APPROVE_FILE_ARTIFACT:
              console.log('[DEBUG] Switch matched APPROVE/REVIEW case, calling approveTask(task, false)')
              return approveTask(task, false)

            default:
              console.log(
                '[DEBUG] Switch hit DEFAULT case - no matching workflowStepType! Value:',
                task.workflowStepType
              )
              return undefined
          }
        })()

        console.log('[DEBUG] taskApproval result:', taskApproval)
        // LEGACY PARITY: if taskApproval, recurse; else reject
        if (taskApproval) {
          console.log('[DEBUG] taskApproval is truthy, attaching .then handlers')
          taskApproval.then(
            () => {
              console.log('[DEBUG] taskApproval resolved, recursing autorunTaskApprovals')
              autorunTaskApprovals(wf, fileToUpload).then(def.resolve, def.reject)
            },
            (err) => {
              console.log('[DEBUG] taskApproval rejected with error:', err)
              def.reject(err)
            }
          )
        } else {
          console.log('[DEBUG] taskApproval is falsy (undefined), rejecting')
          def.reject(new Error('No matching workflow step type'))
        }
      })
      .catch((err) => {
        console.log('[DEBUG] getActiveTaskForWFStepType catch - error:', err)
        def.reject(err)
      })

    return def.promise
  }

  // ============================================================================
  // runPostUploadSequence - Special flow for RECLASSIFY_DOCUMENT only
  // LEGACY PARITY: Lines 245-261
  // ============================================================================
  const runPostUploadSequence = (wf: DetailedWorkflow): Promise<void> => {
    const def = createDeferred<void>()

    getActiveTaskForWFStepType(wf.id)
      .then((task: Task) => {
        const targetFsi = task.files?.[0]?.draftFsi

        // LEGACY PARITY: Check _.isArray and canChangeProperties flag
        if (isArray(task.files) && targetFsi && task.flags?.canChangeProperties) {
          showEditProperties(targetFsi.id, task, true)
            .finally(() => {
              // LEGACY PARITY: isTaskEditEnabled returns Promise but used as truthy check
              // This is a known quirk - KEEP AS-IS
              // @ts-expect-error to match legacy behavior
              if (isTaskEditEnabled(wf)) {
                return approveTask(task, true)
              }
            })
            .finally(def.resolve)
        } else {
          def.resolve()
        }
      })
      .catch(() => {
        // LEGACY PARITY: On error, still resolve
        def.resolve()
      })

    return def.promise
  }

  // ============================================================================
  // performStageAuthorize - Shows WorkflowAuthorizationInfoModal
  // LEGACY PARITY: Lines 263-274 in useWorkflowService.jsx
  // After user confirms in modal, calls approveTask(task, true) which handles e-signature internally
  // ============================================================================
  const performStageAuthorize = (task: Task): Promise<void> => {
    console.log('[DEBUG] performStageAuthorize called with task:', task)
    const def = createDeferred<void>()

    // Dynamic import to match legacy pattern
    console.log('[DEBUG] Importing WorkflowAuthorizationInfoModal...')
    import('../../pages/browser/add-document/WorkflowAuthorizationInfoModal')
      .then(({ default: WorkflowAuthorizationInfoModal }) => {
        console.log('[DEBUG] WorkflowAuthorizationInfoModal loaded, calling showModal')
        showModal(
          <WorkflowAuthorizationInfoModal
            onSuccess={() => {
              console.log('[DEBUG] WorkflowAuthorizationInfoModal onSuccess called')
              // LEGACY PARITY: approveTask(task, true).then(def.resolve, def.reject)
              // approveTask handles e-signature internally via signAction
              approveTask(task, true).then(def.resolve, def.reject)
            }}
            onClose={() => {
              console.log('[DEBUG] WorkflowAuthorizationInfoModal onClose called')
              def.reject()
            }}
          />
        )
      })
      .catch((err) => {
        console.log('[DEBUG] Error importing WorkflowAuthorizationInfoModal:', err)
        def.reject(err)
      })

    return def.promise
  }

  // ============================================================================
  // performStageEdit - Handles REVISE/EDIT workflow steps
  // LEGACY PARITY: Lines 276-315
  // ⚠️ BUG PRESERVED: When fileToUpload is provided, UploadFileRequestModal JSX
  //    is created but showModal() is NEVER called. This is intentional parity.
  // ============================================================================
  const performStageEdit = (wf: DetailedWorkflow, task: Task, fileToUpload?: File): Promise<void> => {
    const def = createDeferred<void>()
    const defUpload = createDeferred<number>()

    // ========================================================================
    // ⚠️ BUG PRESERVED (LEGACY PARITY): Lines 278-289
    // When fileToUpload is provided, JSX is created but showModal() is NOT called
    // This code path effectively does nothing - NO BUG HAS BEEN RAISED IN PROD
    // ========================================================================
    if (fileToUpload) {
      // LEGACY BUG: This JSX is created but never rendered/shown

      import('../../pages/browser/upload-file/UploadFileRequestModal').then(({ default: UploadFileRequestModal }) => {
        // NOTE: showModal is NOT called here - this matches legacy exactly
        const _unusedJsx = (
          <UploadFileRequestModal
            parentId={task.workflowDraftFolderId || 0}
            params={{
              task: task,
              workflowId: wf.id,
              fileToUpload: fileToUpload
            }}
            onSuccess={(uploadedFileId: number) => defUpload.resolve(uploadedFileId)}
            onFailure={() => defUpload.reject()}
          />
        )
        // JSX is created but never used - LEGACY PARITY
        void _unusedJsx
      })
    } else {
      // When no fileToUpload, show UploadFileOptionsModal (LEGACY PARITY)
      import('../../pages/browser/upload-file-options/UploadFileOptionsModal').then(
        ({ default: UploadFileOptionsModal }) => {
          const onSuccess = (uploadedFile: any) => {
            if (uploadedFile) {
              defUpload.resolve(uploadedFile.id)
            } else {
              // LEGACY PARITY: "Later" button - reject to skip showEditProperties
              defUpload.reject()
            }
          }

          const onFailure = () => {
            defUpload.reject()
          }

          showModal(
            <UploadFileOptionsModal params={{ task: task, workflow: wf }} onSuccess={onSuccess} onFailure={onFailure} />
          )
        }
      )
    }

    // After upload, show edit properties then approve (LEGACY PARITY)
    defUpload.promise.then((fsiId: number) => {
      showEditProperties(fsiId, task, false).finally(() => {
        approveTask(task, false).then(def.resolve, def.reject)
      })
    }, def.reject)

    return def.promise
  }

  // ============================================================================
  // getActiveTaskForWFStepType - Gets active task for workflow
  // LEGACY PARITY: Lines 317-333
  // ============================================================================
  const getActiveTaskForWFStepType = (wfId: number): Promise<Task> => {
    console.log('[DEBUG] getActiveTaskForWFStepType called with wfId =', wfId)
    const def = createDeferred<Task>()

    baseApiService
      .fetch<{ elements: Task[] }>('/api/tasks', {
        params: {
          taskStatus: TaskStatus.OPEN,
          workflowId: wfId,
          userId: context.service.auth.getUser()?.id
        }
      })
      .then((data) => {
        console.log('[DEBUG] /api/v2/tasks response:', data)
        console.log('[DEBUG] data.elements length:', data.elements?.length)
        if (data.elements && data.elements.length > 0) {
          console.log('[DEBUG] Found task, resolving with:', data.elements[0])
          def.resolve(data.elements[0])
        } else {
          console.log('[DEBUG] No tasks found, rejecting')
          def.reject(new Error('No active tasks found for workflow'))
        }
      })
      .catch((err) => {
        console.log('[DEBUG] /api/v2/tasks fetch error:', err)
        def.reject(err)
      })

    return def.promise
  }

  // ============================================================================
  // approveTask - Approves task with optional modal confirmation
  // LEGACY PARITY: Lines 335-346 in useWorkflowService.jsx
  // When isAlreadyConfirmed=true, calls service.task.approve which internally handles e-signature
  // via checkEsignAndApprove -> Modal.password (see task.service.js lines 180-195)
  //
  // isSimplified affects modal title:
  //   true  -> 'TASKS_APPROVE_H_TITLE'
  //   false -> 'TASKS_APPROVE_TYPE_INFO_H_TITLE'
  // ============================================================================
  const approveTask = (task: Task, isAlreadyConfirmed?: boolean): Promise<void> => {
    console.log('[DEBUG] approveTask called with task.id =', task.id, 'isAlreadyConfirmed =', isAlreadyConfirmed)
    const def = createDeferred<void>()

    if (isAlreadyConfirmed) {
      console.log('[DEBUG] isAlreadyConfirmed=true, calling checkEsignAndApprove')
      // LEGACY PARITY: service.task.approve(task.id, null, null, task)
      // service.task.approve internally handles e-signature via checkEsignAndApprove
      // We replicate that logic here using signAction from useEsignature hook
      const checkEsignAndApprove = async () => {
        try {
          let validation: any = undefined

          // LEGACY PARITY: task.service.js lines 180-195
          // if (task.flags.approveESignatureRequired) { Modal.password(approveTask, msg); }
          console.log('[DEBUG] task.flags?.approveESignatureRequired =', task.flags?.approveESignatureRequired)
          if (task.flags?.approveESignatureRequired) {
            console.log('[DEBUG] E-signature required, calling signAction...')
            // Use signAction from useEsignature hook (like legacy Modal.password)
            validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_TASK_APPROVE')
            console.log('[DEBUG] signAction returned validation:', validation)
          }

          // Make API call to approve task
          console.log('[DEBUG] Calling PATCH /api/tasks/' + task.id)
          await baseApiService.fetch(`/api/tasks/${task.id}`, {
            method: 'PATCH',
            body: {
              status: 'APPROVED',
              ...(validation ? { esignature: { relayState: validation.relayState } } : {})
            }
          })
          console.log('[DEBUG] Task approved successfully')

          toast.success('TASKS_APPROVE_SUCCESS_MSG')
          // Emit updateTables event (LEGACY PARITY: $rootScope.$emit)
          window.dispatchEvent(new CustomEvent('updateTables'))
          window.dispatchEvent(new CustomEvent('updateTasks'))

          def.resolve()
        } catch (error) {
          // E-signature cancelled or API error
          console.log('[DEBUG] checkEsignAndApprove error:', error)
          def.reject(error)
        }
      }
      checkEsignAndApprove()
    } else {
      console.log('[DEBUG] isAlreadyConfirmed=false, showing TaskApproveModal')
      // Show TaskApproveModal (LEGACY PARITY: service.modal.open)
      // LEGACY PARITY: _.extend(task, { isSimplified: true })
      const taskWithSimplified = extend({}, task, { isSimplified: true })

      import('../../components/tasks/TaskApproveModal').then(({ default: TaskApproveModal }) => {
        showModal(
          <TaskApproveModal
            taskId={task.id}
            task={taskWithSimplified}
            params={{ isSimplified: true }}
            onApprove={() => def.resolve()}
            onClose={() => def.reject()}
          />
        )
      })
    }

    return def.promise
  }

  // ============================================================================
  // showEditProperties - Shows FsiPropertiesModal for editing properties
  // LEGACY PARITY: Lines 348-422
  // ⚠️ NOTE: def.resolve() is NOT called at the start (unlike some legacy patterns)
  // ============================================================================
  const showEditProperties = (fsiId: number, task: Task, showDialogDirectly?: boolean): Promise<void> => {
    const def = createDeferred<void>()

    // Fetch file details (LEGACY PARITY: api.files.getDetailsForProperties)
    baseApiService
      .fetch<any>(`/api/files/${fsiId}/properties`)
      .then((allDataProp) => {
        const filePropIds = allDataProp.metadata?.elements?.map((a: any) => a.metadataTemplateId) || []

        // Fetch metadata templates (LEGACY PARITY: api.metadata.getAll)
        baseApiService
          .fetch<{ elements: any[] }>('/api/metadatatemplates', {
            params: { id: filePropIds.length ? filePropIds : 0 }
          })
          .then((resp) => {
            const metadataList = resp.elements || []

            // Assemble metadata (LEGACY PARITY: service.file.assembleMetadata)
            const assembledMetadata = (allDataProp.metadata?.elements || []).map((fMeta: any) => {
              const template = find(metadataList, { id: fMeta.metadataTemplateId })
              return {
                ...fMeta,
                template,
                inherited: !fMeta.id,
                recursive: false,
                // Convert DATETIME values to Date objects
                value: template?.type === 'DATETIME' && fMeta.value ? new Date(fMeta.value) : fMeta.value
              }
            })

            // Count non-editable metadata in CR (LEGACY PARITY)
            const countCR = reduce(
              assembledMetadata,
              (ccr: number, meta: any) => {
                return meta.editableInCR === false ? ccr + 1 : ccr
              },
              0
            )

            // fileId: fsiId,
            // fileProperties: undefined, // Let modal fetch details
            // isModal: true,
            // task,
            // onPropertiesSaved: handlePropertiesSaved,
            // onClose: handleClose

            // Show dialog function (LEGACY PARITY)
            const showDialog = () => {
              import('../../pages/browser/properties/FsiPropertiesModal').then(({ default: FsiPropertiesModal }) => {
                showModal(
                  <FsiPropertiesModal
                    fileId={allDataProp.id}
                    isModal={true}
                    task={task}
                    onPropertiesSaved={() => def.resolve()}
                    // @ts-expect-error legacy parity
                    isModalBackgroundClickable={isModalBackgroundClickableGlobal}
                    isFromNewFileToEtmf={false}
                    onClose={() => def.resolve()}
                  />
                )
              })
            }

            // Check if we should show properties (LEGACY PARITY: Lines 381-420)
            if (
              assembledMetadata.length > 0 &&
              assembledMetadata.length !== countCR &&
              task.flags?.canChangeProperties
            ) {
              // Check for required properties (LEGACY PARITY)
              const hasRequiredProperties = reduce(
                assembledMetadata,
                (hasReqProps: boolean, item: any) => {
                  return (
                    hasReqProps ||
                    (item.requiredOnFSITemplate &&
                      item.checkRequired &&
                      ((item.mixedValue && item.mixedValueRequiredEmpty) || !item.mixedValue))
                  )
                },
                false
              )

              if (hasRequiredProperties || showDialogDirectly) {
                // Apply propertiesValuesGlobal if set (LEGACY PARITY)
                if (propertiesValuesGlobal) {
                  const updatedMetadataItem: any = []

                  for (const key in assembledMetadata) {
                    const metadataItem = assembledMetadata[key]

                    // Handle ARTIFACT type for direct filing (LEGACY PARITY)
                    if (isDirectFiling) {
                      if (metadataItem.template?.type === 'ARTIFACT') {
                        metadataItem.value = artifactIdForDirectFiling
                      }
                    }

                    // Map properties values (LEGACY PARITY)
                    const propertiesNameKeys = Object.keys(propertiesValuesGlobal)
                    propertiesNameKeys?.forEach((propertyName) => {
                      if (metadataItem.template?.name === propertyName) {
                        if (metadataItem.template.type === 'DATETIME') {
                          metadataItem.value = transformDateForUI(cloneDeep(propertiesValuesGlobal![propertyName]))
                        } else {
                          metadataItem.value = propertiesValuesGlobal![propertyName]
                        }
                      }
                    })
                    updatedMetadataItem.push(metadataItem)
                  }

                  // Save updated properties (LEGACY PARITY: api.files.modifyFileMetadatas)
                  baseApiService
                    .fetch(`/api/files/${allDataProp.id}/metadata`, {
                      method: 'PATCH',
                      body: updatedMetadataItem
                    })
                    .then(() => {
                      showDialog()
                      def.resolve()
                      return def.promise
                    })
                    .catch(() => def.resolve())
                } else {
                  showDialog()
                }
              } else {
                // Show ConfirmEditPropertiesModal first (LEGACY PARITY)
                import('../../pages/browser/properties/ConfirmEditPropertiesModal').then(
                  ({ default: ConfirmEditPropertiesModal }) => {
                    showModal(
                      <ConfirmEditPropertiesModal
                        onConfirm={() => {
                          hideModal()
                          showDialog()
                        }}
                        onCancel={() => {
                          hideModal()
                          def.resolve()
                        }}
                      />
                    )
                  }
                )
              }
            } else {
              def.resolve()
            }
          })
          .catch(() => def.resolve())
      })
      .catch(() => def.resolve())

    return def.promise
  }

  // ============================================================================
  // isTaskEditEnabled - Checks if current user can edit task
  // LEGACY PARITY: Lines 424-447
  // Returns Promise that resolves (true) or rejects (false)
  // ⚠️ NOTE: This returns a Promise but legacy code uses it as boolean in some places
  // ============================================================================
  const isTaskEditEnabled = (workflow: DetailedWorkflow): Promise<void> => {
    const def = createDeferred<void>()

    // Find collaborators step (LEGACY PARITY: _.find with _.includes)
    const collaborators = find(workflow.workflowSteps, (wfs: any) => {
      return includes(
        [
          WorkflowStepType.REVISE_EDIT,
          WorkflowStepType.REVISE_EDIT_MULTIFILE,
          WorkflowStepType.REVISE_EDIT_MIGRATION,
          WorkflowStepType.REVISE_EDIT_RECLASSIFICATION,
          WorkflowStepType.REVISE_EDIT_FILE_ARTIFACT
        ],
        wfs.type
      )
    })

    // Get current user ID
    const authInfo = localStorage.getItem('authInfo')
    const userId = authInfo ? JSON.parse(authInfo)?.user?.id : null

    // Check if single collaborator is current user (LEGACY PARITY)
    if (
      collaborators &&
      collaborators.taskTeamSteps?.length === 1 &&
      collaborators.taskTeamSteps[0].taskTeamItems?.length === 1 &&
      collaborators.taskTeamSteps[0].taskTeamItems[0].userId === userId
    ) {
      def.resolve()
    } else {
      def.reject()
    }

    return def.promise
  }

  // ============================================================================
  // RETURN - All exported functions (LEGACY PARITY: exact same exports)
  // ============================================================================
  return {
    isEosWf,
    getWorkingFileFromWF,
    createCr,
    autorunTaskApprovals,
    runPostUploadSequence,
    performStageAuthorize,
    performStageEdit,
    getActiveTaskForWFStepType,
    approveTask,
    showEditProperties,
    isTaskEditEnabled
  }
}

export default useWorkflowServiceNew

// ============================================================================
// GLOBAL STATE GETTERS/SETTERS (for testing and external access if needed)
// ============================================================================
export const getGlobalState = () => ({
  isDirectFiling,
  artifactIdForDirectFiling,
  isFromNewFileToEtmf,
  propertiesValuesGlobal,
  isModalBackgroundClickableGlobal
})

export const resetGlobalState = () => {
  isDirectFiling = false
  artifactIdForDirectFiling = null
  isFromNewFileToEtmf = false
  propertiesValuesGlobal = null
  isModalBackgroundClickableGlobal = false
}
